// ============================================================================
// launcherStorage — ЕДИНЫЙ слой данных лаунчера (SSoT/SRP).
//
// Проблема: «Новое окно»/«Перезапустить» (desktop, tray.go) запускают процесс с
// НОВЫМ профилем WebView2 (--instance=<uuid>) → ПУСТОЙ localStorage. Список
// серверных баз/центров/ярлыков и последний URL жили в localStorage КАЖДОГО
// окна, а профили WebView2 не делятся между одновременными окнами → в новом
// окне вкладка «Серверные» пуста. Данные лаунчера обязаны быть ОДНИМИ на машину.
//
// Решение — общий файл данных лаунчера, единый для ВСЕХ экземпляров desktop:
//   desktop  → %APPDATA%\HydraDatacentre\launcher.json через Go-мост
//              (App.LoadLauncherData/SaveLauncherData; мутекс + атомарная
//              запись temp+rename — см. desktop/HydraDatacentreDesktop/launcher.go);
//   web/PWA  → localStorage (как было; поведение не меняется — файл не используется).
//
// Инвариант: LAUNCHER_KEYS — ОДИН список ключей, общих для всех окон desktop.
// Запись: read-modify-write (перечитываем файл перед записью) — изменения разных
// окон не теряются. hydration — ДО первого рендера (main.tsx).
// ============================================================================

import { isWailsMode, loadLauncherFile, saveLauncherFile } from '@/lib/wailsBridge';
import { createLogger } from '@/lib/logger';

const lsLog = createLogger('LauncherStorage');

// Ключи данных лаунчера, ОБЩИЕ для всех экземпляров desktop.
// * hydra_centers        — ярлыки серверных баз (url/slug/name/dbType)
// * hydra-center-storage — zustand persist: centerUrl + выбранная база сессии
// * hydra_bases          — offline-кэш списка баз (лаунчер без сети)
// * hydra_last_login     — последний логин (предзаполнение формы входа)
export const LAUNCHER_KEYS = [
  'hydra_centers',
  'hydra-center-storage',
  'hydra_bases',
  'hydra_last_login',
] as const;

// hydratePromise — ЕДИНАЯ гидратация файла лаунчера (SSoT): конкурентные вызовы
// (main.tsx + initApp) ждут ОДИН и тот же результат — рендер не опережает чтение.
let hydratePromise: Promise<void> | null = null;
// writeChain — сериализация записей файла (порядок = порядок изменений).
let writeChain: Promise<void> = Promise.resolve();

export function isLauncherKey(name: string): boolean {
  return (LAUNCHER_KEYS as readonly string[]).includes(name);
}

/** Чтение значения (localStorage-зеркало; на desktop синхронизировано с файлом). */
export function launcherGetItem(name: string): string | null {
  try {
    return localStorage.getItem(name);
  } catch (e) {
    lsLog.warn('localStorage.getItem недоступен', { key: name, error: errText(e) });
    return null;
  }
}

/** Запись значения: localStorage (всегда) + общий файл (desktop, ключи лаунчера). */
export function launcherSetItem(name: string, value: string): void {
  try {
    localStorage.setItem(name, value);
  } catch (e) {
    lsLog.warn('localStorage.setItem недоступен', { key: name, error: errText(e) });
    return;
  }
  if (isLauncherKey(name)) mirrorToFile();
}

/** Удаление значения: localStorage (всегда) + общий файл (desktop, ключи лаунчера). */
export function launcherRemoveItem(name: string): void {
  try {
    localStorage.removeItem(name);
  } catch (e) {
    lsLog.warn('localStorage.removeItem недоступен', { key: name, error: errText(e) });
  }
  if (isLauncherKey(name)) mirrorToFile();
}

/**
 * Гидратировать localStorage-зеркало из общего файла (desktop) ДО первого
 * рендера. Файла нет → ПЕРЕНОСИМ текущий localStorage (штатный профиль
 * WebView2) в общий файл (миграция). web/PWA — no-op (localStorage как был).
 *
 * Идемпотентно: повторные вызовы ждут ТУ ЖЕ гидратацию (без гонки «рендер
 * опередил чтение файла»).
 */
export function hydrateLauncherStorage(): Promise<void> {
  if (!isWailsMode()) return Promise.resolve(); // web/PWA: общий файл не используется
  if (hydratePromise) return hydratePromise;
  hydratePromise = runHydration();
  return hydratePromise;
}

async function runHydration(): Promise<void> {
  let raw = '';
  try {
    raw = await loadLauncherFile();
  } catch (e) {
    lsLog.warn('не удалось прочитать файл лаунчера', { error: errText(e) });
    return;
  }

  if (!raw) {
    // Первый запуск после обновления: файла ещё нет — создаём его из текущего
    // localStorage (миграция). Дальше им пользуются ВСЕ окна.
    mirrorToFile();
    await writeChain;
    lsLog.info('общий файл лаунчера создан (миграция localStorage)', {});
    return;
  }

  let doc: Record<string, unknown>;
  try {
    const parsed = JSON.parse(raw);
    doc = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch (e) {
    // Файл производный от localStorage — повреждённый перезаписываем заново.
    lsLog.warn('файл лаунчера повреждён — перезапись из localStorage', { error: errText(e) });
    doc = {};
  }

  let applied = 0;
  for (const key of LAUNCHER_KEYS) {
    const value = doc[key];
    if (typeof value !== 'string') continue;
    try {
      localStorage.setItem(key, value);
      applied++;
    } catch (e) {
      lsLog.warn('не удалось применить ключ лаунчера', { key, error: errText(e) });
    }
  }
  lsLog.info('данные лаунчера загружены из общего файла', { keys: applied });
}

/** Дождаться завершения отложенных записей файла (вызывать на pagehide/hidden). */
export function flushLauncherStorage(): Promise<void> {
  return writeChain;
}

// ---------------------------------------------------------------------------
// Внутреннее: зеркалирование ключей лаунчера в общий файл (read-modify-write).
// ---------------------------------------------------------------------------

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function mirrorToFile(): void {
  if (!isWailsMode()) return;
  writeChain = writeChain.then(persistLauncherFile).catch((e) => {
    lsLog.warn('не удалось сохранить файл лаунчера', { error: errText(e) });
  });
}

async function persistLauncherFile(): Promise<void> {
  let doc: Record<string, unknown> = {};
  try {
    const raw = await loadLauncherFile();
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        doc = parsed as Record<string, unknown>;
      }
    }
  } catch (e) {
    // Файла нет (первый запуск) или повреждён — начинаем с пустого документа,
    // ниже он заполняется ТЕКУЩИМИ значениями localStorage (файл производный).
    lsLog.info('файл лаунчера отсутствует/повреждён — перезапись', { error: errText(e) });
    doc = {};
  }
  for (const key of LAUNCHER_KEYS) {
    const value = launcherGetItem(key);
    if (value === null) delete doc[key];
    else doc[key] = value;
  }
  await saveLauncherFile(JSON.stringify(doc, null, 2));
}
